/**
 * Generates a small WebP thumbnail from an image File using a canvas.
 * Used at upload time so we can store a tiny preview alongside the original
 * and avoid downloading multi-MB images for 32–48px display in tables/cards.
 *
 * Returns a File (not a Blob) so it can be passed to the same s3uploader
 * as the original — naming is `<originalName>_thumb.webp`.
 *
 * @param {File} file       The original image file
 * @param {number} maxWidth Max thumbnail width in pixels (default 256)
 * @returns {Promise<File>} A thumbnail File
 */
export const generateImageThumbnail = (file, maxWidth = 256) =>
  new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith("image/")) {
      reject(new Error("Not an image file"));
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error("toBlob returned null"));
              return;
            }
            const thumbName = `${file.name.replace(/\.[^.]+$/, "")}_thumb.webp`;
            resolve(new File([blob], thumbName, { type: "image/webp" }));
          },
          "image/webp",
          0.75
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Forces a file download for the given URL.
// Tries blob-fetch first (works cross-origin if the server sends CORS headers).
// Falls back to opening the URL in a new tab if fetch is blocked.
export const downloadMediaFile = async (url, filename) => {
  if (!url) return;
  const fileName =
    filename || decodeURIComponent(url.split("/").pop()?.split("?")[0] || "download");
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[media-download] fetch failed, opening in new tab", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
};
